'use client';

import { useState } from 'react';
import { TbBriefcase } from 'react-icons/tb';

interface JobSection {
  kicker: string;
  paragraph?: string;
  items?: string[];
  star?: boolean;
}

interface Job {
  id: string;
  title: string;
  tags: string[];
  sections: JobSection[];
  applyNote: string;
  linkedin: string;
  emailSubject: string;
}

const EMAIL = 'vagas@trakd.pro';

const JOBS: Job[] = [
  {
    id: 'inside-sales',
    title: 'Executivo(a) de Vendas (Inside Sales)',
    tags: ['Inside Sales', 'SaaS', '100% Remoto · BR', 'PJ · fixo + comissão'],
    sections: [
      {
        kicker: 'sobre a trakd',
        paragraph:
          'A Trakd é uma startup brasileira de tracking server-side para quem vive de Meta Ads. Na prática: a gente devolve para o Facebook os dados de venda que o navegador, o iOS e o bloqueio de cookies escondem, para que o algoritmo otimize melhor e o anunciante pare de "voar às cegas". Nossos clientes são lojistas de e-commerce, infoprodutores, gestores de tráfego e agências que investem pesado em anúncios. Estamos montando nosso time comercial do zero: você vai ajudar a construí-lo.',
      },
      {
        kicker: 'o que você vai fazer',
        items: [
          'Prospectar e qualificar empresas que rodam tráfego pago (inbound + outbound).',
          'Conduzir calls de diagnóstico mostrando, na prática, quanto de venda o cliente perde hoje no rastreamento.',
          'Fechar novos assinantes e acompanhar a ativação dos primeiros dias.',
          'Manter o CRM organizado e o follow-up em dia.',
          'Trazer pro time o que ouve do mercado (objeções, pedidos, concorrentes).',
        ],
      },
      {
        kicker: 'o perfil que procuramos',
        items: [
          'Experiência com vendas consultivas, de preferência em SaaS, martech, adtech, agência de tráfego ou ferramentas para e-commerce.',
          'Entende (ou tem muita vontade de aprender) o universo de Meta Ads, pixel, conversões e marketing de performance.',
          'Perfil "hunter": gosta de meta, comissão e de tocar o próprio jogo.',
          'Autonomia e disciplina para trabalhar 100% remoto.',
          'Boa comunicação escrita e falada.',
        ],
      },
      {
        kicker: 'diferenciais (não obrigatórios)',
        star: true,
        items: [
          'Já foi gestor de tráfego ou trabalhou em agência.',
          'Já vendeu produto recorrente (assinatura/SaaS).',
          'Familiaridade com CAPI, UTMs, pixels, atribuição.',
        ],
      },
      {
        kicker: 'como trabalhamos',
        items: [
          '100% remoto, de qualquer lugar do Brasil.',
          'Contratação PJ.',
          'Remuneração: fixo a combinar + comissão recorrente sobre as assinaturas que você trouxer (você ganha enquanto o cliente fica ativo).',
          'Horário flexível, foco em resultado e não em bater ponto.',
        ],
      },
    ],
    applyNote:
      'conecte-se e mande uma mensagem contando, em poucas linhas, a melhor venda que você já fechou e por que ela foi sua melhor. Quem fizer isso vai direto pro topo da nossa lista.',
    linkedin: 'https://www.linkedin.com/jobs/view/4421196298',
    emailSubject: 'Vaga: Executivo(a) de Vendas (Inside Sales)',
  },
];

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const STAR = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 3l2.5 5 5.5.8-4 3.9 1 5.5L12 16l-5 2.6 1-5.5-4-3.9 5.5-.8z" />
  </svg>
);

export function VagasAccordion() {
  const [openId, setOpenId] = useState<string | null>(JOBS[0]?.id ?? null);

  return (
    <div className="joblist">
      {JOBS.map((job) => {
        const open = openId === job.id;
        return (
          <div key={job.id} className={open ? 'job open' : 'job'}>
            <button
              type="button"
              className="jobhead"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : job.id)}
            >
              <span className="jobicon">
                <TbBriefcase />
              </span>
              <span className="jobmeta">
                <span className="jobtitle">{job.title}</span>
                <span className="jobtags">
                  <span className="jt live">
                    <span className="dt" />
                    Aberta
                  </span>
                  {job.tags.map((t) => (
                    <span key={t} className="jt">
                      {t}
                    </span>
                  ))}
                </span>
              </span>
              <span className="toggle" aria-hidden>
                <span className="bar h" />
                <span className="bar v" />
              </span>
            </button>

            <div className="jobpanel">
              <div className="panelinner">
                {job.sections.map((sec) => (
                  <div key={sec.kicker} className="block">
                    <div className="kicker">{sec.kicker}</div>
                    {sec.paragraph ? <p>{sec.paragraph}</p> : null}
                    {sec.items ? (
                      <ul>
                        {sec.items.map((it) => (
                          <li key={it}>
                            <span className="ck">{sec.star ? STAR : CHECK}</span>
                            {it}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}

                <div className="applynote">
                  <strong>Como se candidatar:</strong> {job.applyNote}
                </div>
                <div className="applygrid">
                  <a
                    className="applycard lk"
                    href={job.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="ic">
                      <svg viewBox="0 0 512 512" fill="currentColor">
                        <path d="M444.17 32H70.28C49.85 32 32 46.7 32 66.89v374.72C32 461.91 49.85 480 70.28 480h373.78c20.54 0 35.94-18.21 35.94-38.39V66.89C480.12 46.7 464.6 32 444.17 32zM170.87 405.43h-63.79V205.88h63.79zM141 175.54h-.46c-20.54 0-33.84-15.29-33.84-34.43 0-19.49 13.65-34.42 34.65-34.42s33.85 14.82 34.31 34.42c-.01 19.14-13.31 34.43-34.66 34.43zm264.43 229.89h-63.79V296.79c0-26.27-9.34-44.18-32.56-44.18-17.74 0-28.24 12-32.91 23.69-1.75 4.2-2.22 9.92-2.22 15.76v113.37h-63.79V205.88h63.79v27.67c9.26-13.22 23.73-32.32 57.56-32.32 42 0 73.62 27.45 73.62 86.5z" />
                      </svg>
                    </span>
                    <div>
                      <div className="t">Candidatar pelo LinkedIn</div>
                      <div className="d">Conecte-se e conte sua melhor venda.</div>
                    </div>
                  </a>
                  <a
                    className="applycard em"
                    href={`mailto:${EMAIL}?subject=${encodeURIComponent(job.emailSubject)}`}
                  >
                    <span className="ic">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <path d="M22 6l-10 7L2 6" />
                      </svg>
                    </span>
                    <div>
                      <div className="t">Candidatar por e-mail</div>
                      <div className="d">Currículo + contato pra {EMAIL}.</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
